import { createEffect, createMemo, createSignal, onCleanup, onMount, Show } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Icon from '~/components/Icon'
import type { ImageItemType, ImageViewerPropsType } from '~/types/ImageViewer'

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max)

const dist = (a: Touch, b: Touch) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
const mid = (a: Touch, b: Touch) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 })

const ImageViewer = (props: ImageViewerPropsType) => {
  let viewerRef: HTMLDivElement | undefined

  const initialIndex = () => Math.min(props.initialIndex ?? 0, Math.max((props.images?.length || 1) - 1, 0))
  const [index, setIndex] = createSignal(initialIndex())

  // Mantener el índice sincronizado con la imagen activa del padre (galería).
  // El visor se monta una vez; sin esto siempre quedaba en 0.
  createEffect(() => {
    if (props.open) {
      const target = initialIndex()
      if (index() !== target) {
        resetView(false)
        setIndex(target)
      }
    }
  })
  const [scale, setScale] = createSignal(1)
  const [offsetX, setOffsetX] = createSignal(0)
  const [offsetY, setOffsetY] = createSignal(0)
  const [dragX, setDragX] = createSignal(0)
  const [rotation, setRotation] = createSignal(0)
  const [flipX, setFlipX] = createSignal(false)
  const [flipY, setFlipY] = createSignal(false)
  const [animating, setAnimating] = createSignal(false)
  const [, setIsZoomed] = createSignal(false)

  const minZoom = () => props.minZoom ?? 1
  const maxZoom = () => props.maxZoom ?? 5
  const zoomStep = () => props.zoomStep ?? 0.5

  const current = createMemo<ImageItemType | undefined>(() => props.images?.[index()])
  const canPrev = createMemo(() => props.loop || index() > 0)
  const canNext = createMemo(() => props.loop || index() < (props.images?.length || 0) - 1)

  let mode: 'none' | 'pan' | 'swipe' | 'pinch' = 'none'
  let startX = 0
  let startY = 0
  let lastX = 0
  let lastY = 0
  let dragStartX = 0
  let touchStartTime = 0
  let lastTapTime = 0
  let pinchStartDist = 0
  let pinchStartScale = 1
  let pinchStartOffsetX = 0
  let pinchStartOffsetY = 0
  let pinchMidX = 0
  let pinchMidY = 0
  let suppressClick = false

  const maxOffX = (s: number) => {
    const w = viewerRef?.clientWidth ?? 0
    return ((s - 1) * w) / 2
  }
  const maxOffY = (s: number) => {
    const h = viewerRef?.clientHeight ?? 0
    return ((s - 1) * h) / 2
  }

  const resetView = (animate = true) => {
    setAnimating(animate)
    setScale(1)
    setOffsetX(0)
    setOffsetY(0)
    setRotation(0)
    setFlipX(false)
    setFlipY(false)
    setIsZoomed(false)
  }

  const zoomAt = (newScale: number, originX: number, originY: number, refScale: number, baseOffX: number, baseOffY: number) => {
    const rect = viewerRef?.getBoundingClientRect()
    if (!rect) return
    const cx = rect.width / 2
    const cy = rect.height / 2
    const ratio = newScale / refScale
    const newOffX = (originX - cx) * (1 - ratio) + baseOffX * ratio
    const newOffY = (originY - cy) * (1 - ratio) + baseOffY * ratio
    setScale(newScale)
    setOffsetX(clamp(newOffX, -maxOffX(newScale), maxOffX(newScale)))
    setOffsetY(clamp(newOffY, -maxOffY(newScale), maxOffY(newScale)))
    setIsZoomed(newScale > 1.001)
  }

  const zoomByButtons = (dir: 1 | -1) => {
    const rect = viewerRef?.getBoundingClientRect()
    const cx = rect ? rect.width / 2 : 0
    const cy = rect ? rect.height / 2 : 0
    const target = clamp(scale() + dir * zoomStep(), minZoom(), maxZoom())
    setAnimating(true)
    zoomAt(target, cx, cy, scale(), offsetX(), offsetY())
  }

  const goPrev = () => {
    if (!canPrev()) return
    setAnimating(true)
    const next = index() > 0 ? index() - 1 : (props.images?.length || 1) - 1
    setDragX(0)
    resetView(false)
    setIndex(next)
    props.onIndexChange?.(next)
  }

  const goNext = () => {
    if (!canNext()) return
    setAnimating(true)
    const next = index() < (props.images?.length || 1) - 1 ? index() + 1 : 0
    setDragX(0)
    resetView(false)
    setIndex(next)
    props.onIndexChange?.(next)
  }

  const applyRotation = (delta: number) => {
    setAnimating(true)
    setRotation(r => (r + delta) % 360)
  }

  const handleDoubleTap = (x: number, y: number) => {
    setAnimating(true)
    if (scale() > 1.001) {
      resetView(true)
    } else {
      zoomAt(2, x, y, 1, 0, 0)
    }
  }

  const handleWheel = (e: WheelEvent) => {
    e.preventDefault()
    const rect = viewerRef?.getBoundingClientRect()
    if (!rect) return
    const originX = e.clientX - rect.left
    const originY = e.clientY - rect.top
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15
    const newScale = clamp(scale() * factor, minZoom(), maxZoom())
    setAnimating(false)
    zoomAt(newScale, originX, originY, scale(), offsetX(), offsetY())
  }

  const handleClose = () => {
    props.onClose?.()
  }

  const handleTouchStart = (e: TouchEvent) => {
    if (props.enableSwipe === false && e.touches.length < 2) return
    const rect = viewerRef?.getBoundingClientRect()
    if (!rect) return

    if (e.touches.length === 2) {
      mode = 'pinch'
      pinchStartDist = dist(e.touches[0], e.touches[1])
      pinchStartScale = scale()
      pinchStartOffsetX = offsetX()
      pinchStartOffsetY = offsetY()
      const m = mid(e.touches[0], e.touches[1])
      pinchMidX = m.x - rect.left
      pinchMidY = m.y - rect.top
      setAnimating(false)
    } else if (e.touches.length === 1) {
      const t = e.touches[0]
      startX = t.clientX
      startY = t.clientY
      lastX = t.clientX
      lastY = t.clientY
      dragStartX = dragX()
      touchStartTime = Date.now()
      mode = scale() > 1.001 ? 'pan' : 'swipe'
      setAnimating(false)
    }
  }

  const handleTouchMove = (e: TouchEvent) => {
    if (mode === 'none') return
    e.preventDefault()

    if (mode === 'pinch' && e.touches.length >= 2) {
      const d = dist(e.touches[0], e.touches[1])
      const newScale = clamp((pinchStartScale * d) / pinchStartDist, minZoom(), maxZoom())
      zoomAt(newScale, pinchMidX, pinchMidY, pinchStartScale, pinchStartOffsetX, pinchStartOffsetY)
    } else if (mode === 'pan' && e.touches.length === 1) {
      const t = e.touches[0]
      const dx = t.clientX - lastX
      const dy = t.clientY - lastY
      lastX = t.clientX
      lastY = t.clientY
      setOffsetX(clamp(offsetX() + dx, -maxOffX(scale()), maxOffX(scale())))
      setOffsetY(clamp(offsetY() + dy, -maxOffY(scale()), maxOffY(scale())))
    } else if (mode === 'swipe' && e.touches.length === 1) {
      const t = e.touches[0]
      setDragX(dragStartX + (t.clientX - startX))
    }
  }

  const handleTouchEnd = (e: TouchEvent) => {
    if (mode === 'pinch') {
      if (e.touches.length === 1) {
        const t = e.touches[0]
        mode = scale() > 1.001 ? 'pan' : 'swipe'
        lastX = t.clientX
        lastY = t.clientY
        startX = t.clientX
        startY = t.clientY
      } else {
        mode = 'none'
      }
      return
    }

    const t = e.changedTouches[0]
    const moved = Math.hypot(t.clientX - startX, t.clientY - startY)
    const dt = Date.now() - touchStartTime

    if (mode === 'swipe') {
      setAnimating(true)
      const threshold = Math.min(120, (viewerRef?.clientWidth ?? 300) * 0.2)
      if (moved < 10) {
        setDragX(0)
      } else if (dragX() <= -threshold && canNext()) {
        goNext()
      } else if (dragX() >= threshold && canPrev()) {
        goPrev()
      } else {
        setDragX(0)
      }
    } else if (mode === 'pan') {
      setAnimating(true)
    }

    if (moved < 10 && dt < 250) {
      const rect = viewerRef?.getBoundingClientRect()
      const now = Date.now()
      if (now - lastTapTime < 300 && rect) {
        handleDoubleTap(t.clientX - rect.left, t.clientY - rect.top)
        lastTapTime = 0
        suppressClick = true
        setTimeout(() => (suppressClick = false), 300)
      } else {
        lastTapTime = now
      }
    }

    mode = 'none'
  }

  const handleTouchCancel = () => {
    mode = 'none'
    setAnimating(true)
    setDragX(0)
  }

  const handleBackdropClick = (e: MouseEvent) => {
    if (suppressClick) {
      suppressClick = false
      return
    }
    if (e.target === e.currentTarget) handleClose()
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault()
      if (e.key === 'ArrowLeft') goPrev()
      else goNext()
    } else if (e.key === '+' || e.key === '=') zoomByButtons(1)
    else if (e.key === '-' || e.key === '_') zoomByButtons(-1)
    else if (e.key === '0') resetView(true)
    else if (e.key === 'r' || e.key === 'R') applyRotation(90)
    else if (e.key === 'Escape') handleClose()
  }

  onMount(() => {
    if (!viewerRef) return
    const onKey = (e: KeyboardEvent) => onKeyDown(e)
    document.addEventListener('keydown', onKey)
    onCleanup(() => {
      document.removeEventListener('keydown', onKey)
    })
  })

  const transform = createMemo(() => `translate(${dragX() + offsetX()}px, ${offsetY()}px) rotate(${rotation()}deg) scale(${scale() * (flipX() ? -1 : 1)}, ${scale() * (flipY() ? -1 : 1)})`)

  const transition = createMemo(() => (animating() ? 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)' : 'none'))

  return (
    <Show when={props.open !== false}>
      <div class={twMerge('fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md', props.class)} onClick={handleBackdropClick}>
        <button
          type="button"
          class="absolute right-4 top-4 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white/80 transition hover:bg-black/70 hover:text-white"
          aria-label="Cerrar visor"
          onClick={handleClose}
        >
          <Icon icon="material-symbols-light:close" size={24} />
        </button>

        <Show when={props.images && props.images.length > 1}>
          <Show when={canPrev()}>
            <button
              type="button"
              class="absolute left-4 top-1/2 z-50 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-slate-800/90 text-white/80 shadow-lg backdrop-blur-sm transition hover:bg-slate-700 hover:text-white sm:left-6"
              aria-label="Imagen anterior"
              onClick={e => {
                e.stopPropagation()
                goPrev()
              }}
            >
              <Icon icon="material-symbols-light:chevron-left" size={28} />
            </button>
          </Show>

          <Show when={canNext()}>
            <button
              type="button"
              class="absolute right-4 top-1/2 z-50 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-slate-800/90 text-white/80 shadow-lg backdrop-blur-sm transition hover:bg-slate-700 hover:text-white sm:right-6"
              aria-label="Imagen siguiente"
              onClick={e => {
                e.stopPropagation()
                goNext()
              }}
            >
              <Icon icon="material-symbols-light:chevron-right" size={28} />
            </button>
          </Show>
        </Show>

        <div
          ref={viewerRef}
          class="relative h-full w-full overflow-hidden select-none"
          style={{ 'touch-action': 'none' }}
          tabindex={0}
          role="dialog"
          aria-modal="true"
          aria-label="Visor de imágenes"
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
        >
          <div class="absolute inset-0 flex items-center justify-center p-16">
            <img
              src={current()?.src}
              alt={current()?.alt ?? current()?.caption ?? `Imagen ${index() + 1}`}
              draggable={false}
              class="max-h-full max-w-full object-contain will-change-transform shadow-2xl"
              style={{
                transform: transform(),
                transition: transition(),
                'transform-origin': 'center center',
                'user-select': 'none',
                '-webkit-user-drag': 'none'
              }}
            />
          </div>

          <Show when={props.showCaption !== false && current()?.caption}>
            <div class="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-black/60 to-transparent px-6 pb-20 pt-4 text-center">
              <p class="text-sm font-medium text-white/90 drop-shadow-lg">{current()?.caption}</p>
            </div>
          </Show>

          <Show when={props.showControls !== false}>
            <div class="absolute inset-x-0 bottom-6 flex items-center justify-center">
              <div class="flex items-center gap-1 rounded-full bg-slate-800/90 px-2 py-2 shadow-xl backdrop-blur-sm">
                <button
                  type="button"
                  class="flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white"
                  aria-label="Aumentar zoom"
                  onClick={() => zoomByButtons(1)}
                >
                  <Icon icon="material-symbols-light:zoom-in" size={20} />
                </button>
                <button
                  type="button"
                  class="flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white"
                  aria-label="Reducir zoom"
                  onClick={() => zoomByButtons(-1)}
                >
                  <Icon icon="material-symbols-light:zoom-out" size={20} />
                </button>

                <div class="mx-1 h-6 w-px bg-white/10"></div>

                <button
                  type="button"
                  class="flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white"
                  aria-label="Rotar a la izquierda"
                  onClick={() => applyRotation(-90)}
                >
                  <Icon icon="material-symbols-light:rotate-left" size={20} />
                </button>
                <button
                  type="button"
                  class="flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white"
                  aria-label="Rotar a la derecha"
                  onClick={() => applyRotation(90)}
                >
                  <Icon icon="material-symbols-light:rotate-right" size={20} />
                </button>

                <div class="mx-1 h-6 w-px bg-white/10"></div>

                <button
                  type="button"
                  class="flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white"
                  aria-label="Voltear horizontalmente"
                  onClick={() => setFlipX(v => !v)}
                >
                  <Icon icon="material-symbols-light:flip" size={20} />
                </button>
                <button
                  type="button"
                  class="flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white"
                  aria-label="Voltear verticalmente"
                  onClick={() => setFlipY(v => !v)}
                >
                  <Icon icon="material-symbols-light:swap-vert" size={20} />
                </button>
                <button
                  type="button"
                  class="flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white"
                  aria-label="Restablecer vista"
                  onClick={() => resetView(true)}
                >
                  <Icon icon="material-symbols-light:restart-alt" size={20} />
                </button>

                <div class="mx-1 h-6 w-px bg-white/10"></div>

                <span class="px-3 text-xs font-semibold text-white/80">{Math.round(scale() * 100)}%</span>
              </div>
            </div>
          </Show>

          <Show when={props.showThumbnails && props.images && props.images.length > 1}>
            <div class="absolute inset-x-0 bottom-20 flex gap-2 overflow-x-auto px-6 py-2">
              {props.images?.map((img, i) => (
                <button
                  type="button"
                  class={twMerge(
                    'h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 transition hover:border-white/60',
                    i === index() ? 'border-white opacity-100 shadow-lg' : 'border-transparent opacity-50 hover:opacity-100'
                  )}
                  aria-label={`Ir a imagen ${i + 1}`}
                  onClick={() => {
                    setAnimating(true)
                    resetView(false)
                    setIndex(i)
                    props.onIndexChange?.(i)
                  }}
                >
                  <img src={img.src} alt={img.alt ?? img.caption ?? `Miniatura ${i + 1}`} class="h-full w-full object-cover" draggable={false} />
                </button>
              ))}
            </div>
          </Show>
        </div>
      </div>
    </Show>
  )
}

export default ImageViewer
